/** Same-origin Shopify cart adapter for the bundle builder. Local previews never submit a cart request.
 *
 * Two modes, picked from the store's own data:
 *  - bundle: once every size of the unlisted "Build Your Own Tea Bundle" product (2, 3 or 4 teas) has an
 *    amount, the bundle is ONE cart line of that size. Chosen teas are Tea 1…N / _byob_teas properties for
 *    Admin and packing slips. Cart attributes list every builder bundle (Bundle 1 Tea 1…, Bundle 2…)
 *    if a packing slip omits line properties. The checkout order note is left for the shopper. If
 *    Recharge Plus Bundles is configured, an _rb_id is added so Recharge can expand stock; without it,
 *    checkout still proceeds. The variant carries the Recharge 30/60/90-day plans.
 *  - teas: until then, each chosen tea is its own line at its own one-time or 15% subscription price
 *    (the 5 October behaviour, and what Kita's copy doc describes).
 * Each add keeps earlier builder lines (a second bundle is a second parent line) and leaves exactly one
 * Delivery Guarantee line at the store's tier (or none if unticked). After a successful add, the builder
 * either sends the shopper to checkout or resets so they can add another bundle. This file never decides
 * an amount: it reads the store's variants and plan allocations and refuses to sell when one is missing,
 * zero or changed.
 */
(function (browser, factory) {
  'use strict';
  const api = factory(browser);
  if (browser) browser.TeaCheckout = api;
  if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : null, function (browser) {
  'use strict';
  const STORE_HOSTS = ['looseleafteamarket.com', 'www.looseleafteamarket.com'];
  const BUNDLE_HANDLE = 'build-your-own-tea-bundle';
  const PROTECTION_HANDLE = 'gm-shipping-protection';
  const PENDING_KEY = 'lltm-byob-cart-pending-v2';
  // The store's own delivery-guarantee-guard.js reads this key: a shopper who turned the guarantee
  // off in the cart starts with it off here, and a choice made here carries back to the cart.
  const PROTECTION_OFF_KEY = 'shippingProtectionUserDisabled';
  const SIZES = [2, 3, 4];
  const PACKING_MAX = 10;
  let busy = false;
  let pendingInMemory = null;
  let bundle = null;
  let protection = null;
  let rechargeConfig = null;
  let rechargeReady = false;
  const cents = n => Number.isSafeInteger(n) && n >= 0;
  const validId = id => /^\d+$/.test(String(id));
  const cleanText = value => String(value || '').replace(/[\u0000-\u001f<>]/g, '').slice(0, 80);

  function cadence(plan) {
    const match = String(plan.name || '').match(/^Delivery every (30|60|90) days(?: with 15% discount)?$/i);
    return match ? 'every-' + match[1] + '-days' : null;
  }
  /** Only the store's plain 30/60/90-day plans with one 15% adjustment are offered. */
  function supportedPlan(plan) {
    const stages = plan.planPriceAdjustments;
    const allocations = plan.allocationPriceAdjustments;
    return validId(plan.id) && cadence(plan) && cents(plan.priceCents) && plan.priceCents > 0 &&
      plan.recurringDeliveries === true && Array.isArray(stages) && stages.length === 1 &&
      stages[0].order_count === null && stages[0].value_type === 'percentage' && stages[0].value === 15 &&
      Array.isArray(allocations) && allocations.length === 1 && allocations[0].price === plan.priceCents &&
      plan.perDeliveryPriceCents === plan.priceCents;
  }

  function sizeOf(title) {
    const match = String(title || '').match(/^([234]) teas$/i);
    return match ? Number(match[1]) : null;
  }

  /** Normalise the bundle product from Liquid ({variants:[{id,title,cents,available,plans}]}) or from /products/<handle>.js. */
  function normaliseBundle(data) {
    if (!data || !Array.isArray(data.variants)) return null;
    const raw = new Map();
    (data.selling_plan_groups || []).forEach(g => (g.selling_plans || []).forEach(p => raw.set(String(p.id), p)));
    const variants = {};
    data.variants.forEach(v => {
      const size = sizeOf(v.title || v.option1 || v.public_title);
      if (!size || !validId(v.id)) return;
      const plans = Array.isArray(v.plans) ? v.plans.map(a => ({id: String(a.id), name: a.name, priceCents: a.cents,
          recurringDeliveries: a.recurring, planPriceAdjustments: a.planAdj, allocationPriceAdjustments: a.allocAdj,
          perDeliveryPriceCents: a.perDelivery}))
        : (v.selling_plan_allocations || []).filter(a => raw.has(String(a.selling_plan_id))).map(a => {
          const plan = raw.get(String(a.selling_plan_id));
          return {id: String(a.selling_plan_id), name: plan.name, priceCents: a.price,
            recurringDeliveries: plan.recurring_deliveries, planPriceAdjustments: plan.price_adjustments,
            allocationPriceAdjustments: a.price_adjustments, perDeliveryPriceCents: a.per_delivery_price};
        });
      variants[size] = {id: String(v.id), size, cents: v.cents !== undefined ? v.cents : v.price,
        available: v.available === true, plans: plans.filter(supportedPlan)};
    });
    const productId = data.productId || data.id;
    return Object.keys(variants).length ? {handle: data.handle || BUNDLE_HANDLE, productId: validId(productId) ? String(productId) : null, variants} : null;
  }

  function normaliseProtection(data) {
    if (!data || !Array.isArray(data.variants)) return null;
    return {productId: data.productId || data.id || null,
      variants: data.variants.filter(v => validId(v.id)).map(v => ({id: String(v.id),
        cents: v.cents !== undefined ? v.cents : v.price, available: v.available === true}))};
  }

  function configure(options) {
    if (!options) return;
    if (options.bundle !== undefined) bundle = normaliseBundle(options.bundle);
    if (options.protection !== undefined) protection = normaliseProtection(options.protection);
    if (options.recharge !== undefined) {
      const r = options.recharge;
      rechargeConfig = r && validId(r.collectionId) ? {collectionId: String(r.collectionId),
        storeIdentifier: typeof r.storeIdentifier === 'string' ? r.storeIdentifier : '',
        storefrontAccessToken: typeof r.storefrontAccessToken === 'string' ? r.storefrontAccessToken : ''} : null;
    }
  }

  /**
   * Recharge Plus fixed-price bundles: when available, the line gets an _rb_id so Recharge expands the
   * chosen teas and adjusts stock. Without Plus (or without a configured collection) this returns null
   * and checkout still sells the bundle line with Tea 1…N properties for fulfillment.
   */
  async function rechargeBundleId(bundleProductId, variantId, teas) {
    const rc = browser && browser.recharge;
    if (!rechargeConfig || !bundleProductId || !rc || !rc.bundle || typeof rc.bundle.getBundleId !== 'function') {
      return null;
    }
    if (!rechargeReady && typeof rc.init === 'function') {
      const init = {appName: 'lltm-byob', appVersion: '2'};
      if (rechargeConfig.storeIdentifier) init.storeIdentifier = rechargeConfig.storeIdentifier;
      if (rechargeConfig.storefrontAccessToken) init.storefrontAccessToken = rechargeConfig.storefrontAccessToken;
      rc.init(init);
      rechargeReady = true;
    }
    if (teas.some(p => !validId(p.productId))) throw new Error('This tea needs its product link checked.');
    try {
      const selections = [];
      teas.forEach(p => {
        const variantId = String(p.variantId);
        const existing = selections.find(s => s.externalVariantId === variantId);
        if (existing) existing.quantity += 1;
        else selections.push({collectionId: rechargeConfig.collectionId, externalProductId: String(p.productId),
          externalVariantId: variantId, quantity: 1});
      });
      const id = await rc.bundle.getBundleId({
        externalProductId: String(bundleProductId),
        externalVariantId: String(variantId),
        selections
      });
      return typeof id === 'string' && id ? id : null;
    } catch (_) {
      return null;
    }
  }

  function uniqueProducts(products) {
    const seen = new Set();
    return (Array.isArray(products) ? products : []).filter(p => {
      if (!p || seen.has(String(p.variantId))) return false;
      seen.add(String(p.variantId));
      return true;
    });
  }
  function pouchProducts(products) {
    return (Array.isArray(products) ? products : []).filter(Boolean);
  }
  /**
   * Cart attributes for packing slips, rebuilt from every builder line currently in the cart.
   * Bundle 1 is the first one added. Clears leftover numbered slots and the old un-numbered
   * Tea 1–4 / Bundle keys. Never writes cart.note (that is the shopper's checkout note).
   */
  function teaNamesFromProperties(props) {
    const teas = [];
    for (let i = 1; i <= 4; i++) {
      const name = cleanText(props && props['Tea ' + i]);
      if (name) teas.push(name);
    }
    return teas;
  }
  function builderGroups(cart) {
    const seen = [];
    const groups = new Map();
    (cart && cart.items || []).forEach(line => {
      const props = line.properties || {};
      const id = props._byob_request;
      if (!id || props._byob_accessory || groups.has(id)) return;
      seen.push(id);
      const named = teaNamesFromProperties(props);
      const teas = named.length ? named : (cart.items || []).filter(l =>
          l.properties && l.properties._byob_request === id && !l.properties._byob_accessory)
        .slice().reverse().reduce((list, l) => {
          const title = cleanText(l.product_title || l.title);
          const qty = Number(l.quantity) || 1;
          for (let i = 0; i < qty && list.length < 4; i++) if (title) list.push(title);
          return list;
        }, []);
      groups.set(id, {name: cleanText(props.Bundle) || 'Your tea bundle', teas});
    });
    return seen.reverse().map(id => groups.get(id));
  }
  function packingAttributes(cart) {
    const attributes = {'Tea 1': '', 'Tea 2': '', 'Tea 3': '', 'Tea 4': '', Bundle: ''};
    for (let n = 1; n <= PACKING_MAX; n++) {
      attributes['Bundle ' + n] = '';
      for (let t = 1; t <= 4; t++) attributes['Bundle ' + n + ' Tea ' + t] = '';
    }
    builderGroups(cart).slice(0, PACKING_MAX).forEach((group, i) => {
      const n = i + 1;
      attributes['Bundle ' + n] = group.name;
      group.teas.forEach((name, ti) => { attributes['Bundle ' + n + ' Tea ' + (ti + 1)] = name; });
    });
    return attributes;
  }
  function customerNote(existing) {
    return String(existing || '').split(/\r?\n/).reduce((lines, line) => {
      String(line).split(' · ').forEach(part => {
        const text = part.trim();
        if (text && !/^(Tea [1-4]|Bundle(?: \d+(?: Tea [1-4])?)?):/i.test(text)) lines.push(text);
      });
      return lines;
    }, []).join('\n');
  }
  async function applyPackingAttributes(cart) {
    const payload = {attributes: packingAttributes(cart)};
    const current = String(cart && cart.note || '');
    const note = customerNote(current);
    if (note !== current.trim()) payload.note = note;
    try { return await post('cart/update.js', payload); }
    catch (_) { return cart || null; }
  }

  function variantFor(size, source) {
    const b = source || bundle;
    return b && b.variants && b.variants[size] || null;
  }

  /** Bundle pricing is used only once EVERY size has an amount; until then the page sells tea by tea. */
  function bundleActive(source) {
    const b = source || bundle;
    return Boolean(b && b.variants && SIZES.every(n => b.variants[n] && cents(b.variants[n].cents) && b.variants[n].cents > 0));
  }
  function mode(source) { return bundleActive(source) ? 'bundle' : 'teas'; }
  /** One-time price for a bundle size, once every size has an amount. Null until then. */
  function oneTimeCents(size) {
    if (!bundleActive()) return null;
    const variant = variantFor(size);
    return variant && cents(variant.cents) && variant.cents > 0 ? variant.cents : null;
  }
  const sortKeys = keys => [...new Set(keys.filter(Boolean))].sort((a, b) => parseInt(a.split('-')[1], 10) - parseInt(b.split('-')[1], 10));
  function matchingPlan(product, key) {
    return (product.sellingPlans || []).find(plan => supportedPlan(plan) && cadence(plan) === String(key));
  }
  /** Tea-by-tea: only the schedules every chosen tea offers. */
  function sharedPlanKeys(selected) {
    if (!selected.length) return [];
    return sortKeys((selected[0].sellingPlans || []).filter(supportedPlan).map(cadence)
      .filter(key => selected.every(p => matchingPlan(p, key))));
  }

  /** Delivery schedules offered for these teas (or, given a number, for a bundle of that size). */
  function plansFor(arg) {
    const toPlans = keys => keys.map(key => ({id: key, name: 'Every ' + key.split('-')[1] + ' days'}));
    if (Array.isArray(arg)) {
      const pouches = pouchProducts(arg);
      if (mode() === 'teas') return toPlans(sharedPlanKeys(uniqueProducts(pouches)));
      arg = pouches.length;
    }
    const v = variantFor(arg);
    return toPlans(sortKeys(v ? v.plans.map(cadence) : []));
  }

  /** Tea-by-tea quote: one pouch per tea at its own one-time price or its own plan allocation. */
  function quoteTeas(selected, purchaseType, planId, result) {
    result.mode = 'teas';
    result.oneTimeCents = result.separateCents;
    result.subtotalCents = result.separateCents;
    result.planIds = sharedPlanKeys(selected);
    if (!['subscription', 'one-time'].includes(purchaseType)) {
      result.reason = 'Choose a subscription or a one-time purchase.'; return result;
    }
    if (purchaseType === 'one-time' && selected.some(p => p.requiresSellingPlan)) {
      result.reason = 'One of these teas is only available on subscription.'; return result;
    }
    if (purchaseType === 'subscription' && !result.planIds.includes(String(planId))) {
      result.reason = result.planIds.length ? 'Choose a delivery schedule for your subscription.' :
        'These teas do not share a subscription option. Try another tea or choose a one-time purchase.';
      return result;
    }
    const grouped = [];
    selected.forEach(p => {
      const key = String(p.variantId);
      const row = grouped.find(g => g.key === key);
      if (row) row.quantity += 1;
      else grouped.push({key, product: p, quantity: 1});
    });
    result.items = grouped.map(g => {
      const item = {id: String(g.product.variantId), quantity: g.quantity};
      if (purchaseType === 'subscription') item.selling_plan = String(matchingPlan(g.product, planId).id);
      return item;
    });
    result.totalCents = purchaseType === 'subscription' ? selected.reduce((sum, p) => sum + matchingPlan(p, planId).priceCents, 0) : result.separateCents;
    if (!cents(result.totalCents) || result.totalCents <= 0) {
      result.reason = 'We could not calculate this selection. Please choose your teas again.'; return result;
    }
    result.subscriptionSavingCents = Math.max(0, result.separateCents - result.totalCents);
    result.savingCents = result.subscriptionSavingCents;
    result.valid = true;
    return result;
  }

  /** Quote the selection in the store's current mode (or the mode given). */
  function quote(products, purchaseType, planId, source, forceMode) {
    const selected = pouchProducts(products);
    const unique = uniqueProducts(selected);
    const result = {valid: false, reason: '', mode: forceMode || mode(source), size: selected.length, separateCents: 0, oneTimeCents: 0,
      subtotalCents: 0, totalCents: 0, savingCents: 0, bundleSavingCents: 0, subscriptionSavingCents: 0,
      item: null, items: [], planIds: []};
    if (selected.length < 2 || selected.length > 4) {
      result.reason = selected.length === 1 ? 'Pick at least 2 teas for your bundle.' : 'Pick 2 to 4 teas for your bundle.';
      return result;
    }
    if (unique.some(p => !validId(p.variantId) || !cents(p.priceCents) || p.available !== true)) {
      result.reason = 'A selected tea is unavailable. Please choose another tea.'; return result;
    }
    result.separateCents = selected.reduce((sum, p) => sum + p.priceCents, 0);
    if (result.mode === 'teas') return quoteTeas(selected, purchaseType, planId, result);
    const variant = variantFor(selected.length, source);
    result.planIds = variant ? [...new Set(variant.plans.map(cadence))].sort((a, b) => parseInt(a.split('-')[1], 10) - parseInt(b.split('-')[1], 10)) : [];
    if (!variant || !cents(variant.cents) || variant.cents <= 0) {
      result.reason = 'Bundle pricing is not available right now. Please try again shortly.'; return result;
    }
    if (variant.available !== true) {
      result.reason = 'This bundle size is unavailable right now. Please choose a different number of teas.'; return result;
    }
    result.oneTimeCents = variant.cents;
    result.subtotalCents = variant.cents;
    if (!['subscription', 'one-time'].includes(purchaseType)) {
      result.reason = 'Choose a subscription or a one-time purchase.'; return result;
    }
    let plan = null;
    if (purchaseType === 'subscription') {
      plan = variant.plans.find(p => cadence(p) === String(planId));
      if (!plan) {
        result.reason = variant.plans.length ? 'Choose a delivery schedule for your subscription.' :
          'Subscriptions are not available for this bundle right now. Choose a one-time purchase.';
        return result;
      }
    }
    result.totalCents = plan ? plan.priceCents : variant.cents;
    if (!cents(result.totalCents) || result.totalCents <= 0 || result.totalCents > result.oneTimeCents) {
      result.reason = 'We could not calculate this bundle. Please choose your teas again.'; return result;
    }
    result.subscriptionSavingCents = result.oneTimeCents - result.totalCents;
    result.bundleSavingCents = Math.max(0, result.separateCents - result.oneTimeCents);
    result.savingCents = Math.max(0, result.separateCents - result.totalCents);
    result.item = {id: variant.id, quantity: 1};
    if (plan) result.item.selling_plan = String(plan.id);
    result.items = [result.item];
    result.valid = true;
    return result;
  }

  function isLive() {
    return Boolean(browser && browser.TEA_CHECKOUT_MODE === 'shopify' &&
      browser.location.protocol === 'https:' && STORE_HOSTS.includes(browser.location.hostname));
  }

  function root() {
    const value = browser && browser.Shopify && browser.Shopify.routes && browser.Shopify.routes.root;
    return typeof value === 'string' && /^\/(?:[a-zA-Z0-9-]+\/)*$/.test(value) ? value : '/';
  }

  async function request(path, options) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await browser.fetch(root() + path, Object.assign({
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: {'Accept': 'application/json'}
      }, options || {}));
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(cleanText(data.description || data.message) || 'The shop could not complete that request.');
        error.status = response.status;
        throw error;
      }
      return data;
    } finally { clearTimeout(timeout); }
  }
  const post = (path, body) => request(path, {method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}, body: JSON.stringify(body)});

  /** Re-read each tea's variant, stock and plan allocations; never substitute a different size. */
  async function refreshTeas(products) {
    return Promise.all(products.map(async p => {
      const handle = p.handle || p.id;
      if (!/^[a-z0-9-]+$/.test(handle)) throw new Error('This tea needs its product link checked.');
      const data = await request('products/' + handle + '.js');
      const variant = (data.variants || []).find(v => String(v.id) === String(p.variantId));
      if (!variant) return Object.assign({}, p, {available: false, sellingPlans: []});
      const raw = new Map();
      (data.selling_plan_groups || []).forEach(g => (g.selling_plans || []).forEach(plan => raw.set(String(plan.id), plan)));
      const plans = (variant.selling_plan_allocations || []).filter(a => raw.has(String(a.selling_plan_id))).map(a => {
        const plan = raw.get(String(a.selling_plan_id));
        return {id: String(a.selling_plan_id), name: plan.name, priceCents: a.price, compareAtPriceCents: a.compare_at_price,
          recurringDeliveries: plan.recurring_deliveries, planPriceAdjustments: plan.price_adjustments,
          allocationPriceAdjustments: a.price_adjustments, perDeliveryPriceCents: a.per_delivery_price};
      });
      return Object.assign({}, p, {productId: data.id, priceCents: variant.price, available: variant.available === true,
        requiresSellingPlan: variant.requires_selling_plan === true || data.requires_selling_plan === true, sellingPlans: plans});
    }));
  }

  // ---- Delivery Guarantee -------------------------------------------------------------------
  function isProtectionLine(line) {
    const guard = browser && browser.DeliveryGuaranteeGuard;
    if (guard && typeof guard.isProtectionItem === 'function') {
      try { return guard.isProtectionItem(line); } catch (_) { /* fall through to the local check */ }
    }
    if (protection && protection.productId && String(line.product_id) === String(protection.productId)) return true;
    const title = String(line.product_title || line.title || '').toLowerCase();
    return title.includes('delivery guarantee') || title.includes('shipping protection') || String(line.product_type || '').toLowerCase() === 'insurance';
  }
  function merchandiseCents(cart) {
    return (cart.items || []).filter(l => !isProtectionLine(l)).reduce((s, l) => s + Number(l.final_line_price || l.line_price || 0), 0);
  }
  /** The tier comes from the store's own guard so the builder and the cart can never disagree. */
  function protectionTierCents(merchCents) {
    const guard = browser && browser.DeliveryGuaranteeGuard;
    if (guard && typeof guard.expectedProtectionPrice === 'function') {
      const dollars = guard.expectedProtectionPrice(merchCents / 100);
      if (typeof dollars === 'number' && isFinite(dollars)) return Math.round(dollars * 100);
    }
    return null;
  }
  /** The store's ladder is defined in US dollars only, so other currencies get no guarantee line from here. */
  function protectionVariant(merchCents, source, currency) {
    const p = source || protection;
    if (!p || currency !== 'USD') return null;
    const tier = protectionTierCents(merchCents);
    if (tier === null) return null;
    return p.variants.find(v => v.cents === tier && v.available) || null;
  }
  function pageCurrency() {
    const active = browser && browser.Shopify && browser.Shopify.currency && browser.Shopify.currency.active;
    return /^[A-Z]{3}$/.test(String(active)) ? String(active) : 'USD';
  }
  function protectionWanted() {
    try { return browser.localStorage.getItem(PROTECTION_OFF_KEY) !== 'true'; } catch (_) { return true; }
  }
  function rememberProtection(on) {
    try {
      if (on) browser.localStorage.removeItem(PROTECTION_OFF_KEY);
      else browser.localStorage.setItem(PROTECTION_OFF_KEY, 'true');
    } catch (_) { /* The cart still follows the choice made for this order. */ }
  }
  /**
   * Any add to the cart makes Rebuy open its drawer, and the store's guard then starts its own
   * "correct the guarantee" routine, which removes and re-adds the line: the race that produced the
   * duplicate guarantee charges. The guard stands down while the shopper's "off" flag is set, so the
   * flag is held from just before the add until this add is finished, then set to what the shopper
   * actually chose. Returns settle(): call it with true to restore the choice at once.
   */
  function holdGuard(wanted) {
    let done = false;
    try { browser.localStorage.setItem(PROTECTION_OFF_KEY, 'true'); } catch (_) { return () => {}; }
    const restore = () => { if (done) return; done = true; rememberProtection(wanted); };
    return function settle(now) {
      if (now || !wanted) { restore(); return; }
      if (typeof browser.addEventListener === 'function') browser.addEventListener('pagehide', restore, {once: true});
      // If the browser never leaves (blocked navigation), do not strand the shopper with the guarantee off.
      setTimeout(restore, 20000);
    };
  }

  /**
   * What the guarantee line will cost for this order: the cart's current merchandise plus this bundle.
   * Existing builder lines count, because a second add keeps them.
   */
  async function protectionPreview(bundleCents) {
    if (!isLive()) return null;
    try {
      const cart = await request('cart.js');
      const others = (cart.items || []).filter(l => !isProtectionLine(l));
      const otherCents = others.reduce((s, l) => s + Number(l.final_line_price || l.line_price || 0), 0);
      const v = protectionVariant(otherCents + bundleCents, null, cart.currency);
      return v ? {cents: v.cents, otherItems: others.reduce((n, l) => n + Number(l.quantity || 0), 0)} : null;
    } catch (_) { return null; }
  }

  // ---- Pending-request recovery ------------------------------------------------------------
  function loadPending() {
    if (pendingInMemory) return pendingInMemory;
    try { return JSON.parse(browser.sessionStorage.getItem(PENDING_KEY) || 'null'); }
    catch (_) { return null; }
  }
  function savePending(value) {
    pendingInMemory = value;
    try {
      if (value) browser.sessionStorage.setItem(PENDING_KEY, JSON.stringify(value));
      else browser.sessionStorage.removeItem(PENDING_KEY);
    } catch (_) { /* In-memory protection still prevents duplicate clicks in this page. */ }
  }
  const ourLines = (cart, id) => (cart.items || []).filter(line => line.properties && line.properties._byob_request === id);
  /** Every line this request added is in the cart, on the right variant and plan (and with its Recharge bundle ID). */
  function linesPresent(cart, pending) {
    const lines = ourLines(cart, pending.id);
    return (pending.items || []).length > 0 && pending.items.every(wanted => lines.some(line =>
      String(line.variant_id || line.id) === String(wanted.id) && line.quantity >= wanted.quantity &&
      (!pending.rbId || line.properties._rb_id === pending.rbId) &&
      String(line.selling_plan_allocation && line.selling_plan_allocation.selling_plan.id || '') === String(wanted.selling_plan || '')));
  }

  function goToCheckout() { browser.location.assign(root() + 'checkout'); }
  function clearPending() { savePending(null); }

  async function removeLines(cart, id) {
    const updates = {};
    ourLines(cart, id).forEach(line => { updates[line.key] = 0; });
    return Object.keys(updates).length ? post('cart/update.js', {updates}) : cart;
  }

  /**
   * The variant the cart's single guarantee line should be on, or null for none. With no tier known
   * (another currency, or the store's guard is absent) the existing line is kept as the store priced it.
   */
  function protectionTargetId(cart, wanted, source) {
    if (!wanted) return null;
    const target = protectionVariant(merchandiseCents(cart), source, cart.currency);
    if (target) return target.id;
    const first = (cart.items || []).find(isProtectionLine);
    return first ? String(first.variant_id) : null;
  }
  function protectionSettled(cart, targetId) {
    const lines = (cart.items || []).filter(isProtectionLine);
    return targetId ? lines.length === 1 && String(lines[0].variant_id) === targetId && lines[0].quantity === 1 : lines.length === 0;
  }
  /**
   * One cart/update call that touches guarantee lines only: every extra one to 0 and the right one to 1.
   * Removing and re-adding in two calls left a moment with no guarantee, in which the store's guard added another.
   */
  function protectionUpdates(cart, targetId) {
    const updates = {};
    let kept = false;
    (cart.items || []).filter(isProtectionLine).forEach(line => {
      const keep = !kept && targetId && String(line.variant_id) === targetId;
      if (keep) kept = true;
      updates[line.key] = keep ? 1 : 0;
    });
    if (targetId && !kept) updates[targetId] = 1;
    return updates;
  }
  /** Leave the cart with exactly one guarantee line at the right tier (or none), never touching the teas. */
  async function settleProtection(cart, wanted, source) {
    for (let attempt = 0; attempt < 2; attempt++) {
      const targetId = protectionTargetId(cart, wanted, source);
      if (protectionSettled(cart, targetId)) return cart;
      await post('cart/update.js', {updates: protectionUpdates(cart, targetId)});
      cart = await request('cart.js');
    }
    if (protectionSettled(cart, protectionTargetId(cart, wanted, source))) return cart;
    throw new Error('Your bundle is in your cart, but the Delivery Guarantee needs a look. Please check your cart before checkout.');
  }

  /** One-time accessory lines from the builder mini-cart (not part of the Recharge tea bundle). */
  function accessoryQty(value) {
    const n = Number(value);
    return Number.isInteger(n) && n >= 1 && n <= 10 ? n : 1;
  }
  function accessoryLines(list) {
    if (!Array.isArray(list)) return [];
    const seen = new Set();
    return list.filter(a => a && validId(a.id) && !seen.has(String(a.id)) && seen.add(String(a.id)))
      .map(a => ({id: String(a.id), quantity: accessoryQty(a.quantity), selling_plan: '',
        cents: Number.isSafeInteger(a.cents) ? a.cents : 0,
        title: cleanText(a.title || a.name || 'Accessory')}));
  }
  function accessoryCents(list) {
    return accessoryLines(list).reduce((sum, a) => sum + (a.cents || 0) * a.quantity, 0);
  }

  /** Add the bundle, verify it, settle the guarantee (if ticked) to exactly one line, then return so the builder can send the shopper to checkout or start another bundle. */
  async function checkout(products, purchaseType, planId, details, options) {
    if (busy) throw new Error('Your bundle is already being added. Please wait.');
    const wantProtection = !options || options.protection !== false;
    const extras = accessoryLines(options && options.accessories);
    const m = mode();
    let priced = quote(products, purchaseType, planId, null, m);
    if (!priced.valid) throw new Error(priced.reason);
    if (!isLive()) return {preview: true, item: priced.item, items: priced.items, quote: priced, accessories: extras, protection: wantProtection};
    busy = true;
    const settleGuard = holdGuard(wantProtection);
    try {
      const selected = pouchProducts(products);
      const unique = uniqueProducts(selected);
      const fingerprint = JSON.stringify({mode: m, items: priced.items, teas: selected.map(p => p.variantId),
        accessories: extras.map(a => [a.id, a.quantity]), assignments: details && details.assignments || {}, name: cleanText(details && details.name)});
      // Same bundle already fully in the cart from this tab (e.g. they added it, then clicked add again):
      // skip re-adding. Other builder lines already in the cart stay.
      const pending = loadPending();
      const startCart = await request('cart.js');
      if (pending && pending.fingerprint === fingerprint && linesPresent(startCart, pending)) {
        savePending(Object.assign({}, pending, {verified: true}));
        await applyPackingAttributes(startCart);
        await settleProtection(startCart, wantProtection, null);
        return {preview: false, recovered: true};
      }
      savePending(null);
      // Fresh store data: the bundle's variant and plans (bundle mode), the guarantee tiers, each tea's stock and plans.
      const [freshBundle, freshProtection, freshTeas, currentCart] = await Promise.all([
        m === 'bundle' ? request('products/' + BUNDLE_HANDLE + '.js').then(normaliseBundle) : Promise.resolve(null),
        request('products/' + PROTECTION_HANDLE + '.js').then(d => normaliseProtection(Object.assign({productId: d.id}, d))).catch(() => protection),
        refreshTeas(unique),
        request('cart.js')
      ]);
      if (currentCart.currency !== pageCurrency()) throw new Error('Your cart is in a different currency from this page. Refresh the page and try again.');
      const freshPouches = selected.map(p => freshTeas.find(t => t.id === p.id || String(t.variantId) === String(p.variantId)) || p);
      const updated = quote(freshPouches, purchaseType, planId, freshBundle, m);
      if (!updated.valid) throw new Error(updated.reason);
      if (updated.totalCents !== priced.totalCents || JSON.stringify(updated.items) !== JSON.stringify(priced.items)) {
        if (freshBundle) bundle = freshBundle;
        throw new Error('The shop price has changed. Review the updated total, then continue.');
      }
      priced = updated;
      const id = browser.crypto && browser.crypto.randomUUID ? browser.crypto.randomUUID() : Date.now() + '-' + Math.random().toString(36).slice(2);
      const bundleName = cleanText(details && (details.bundleName || details.name)) || 'Your tea bundle';
      let lineItems;
      let rbId = null;
      if (m === 'bundle') {
        rbId = await rechargeBundleId(freshBundle.productId, priced.item.id, freshPouches);
        const properties = {};
        freshPouches.forEach((p, i) => { properties['Tea ' + (i + 1)] = cleanText(p.name); });
        properties['Bundle'] = bundleName;
        properties._byob_request = id;
        properties._byob_teas = freshPouches.map(p => String(p.variantId)).join(',');
        if (rbId) properties._rb_id = rbId;
        lineItems = [Object.assign({}, priced.item, {properties})];
      } else {
        lineItems = priced.items.map(item => Object.assign({}, item, {
          properties: {'Bundle': bundleName, '_byob_request': id}
        }));
      }
      extras.forEach(a => {
        lineItems.push({id: a.id, quantity: a.quantity, properties: {
          Bundle: bundleName, _byob_request: id, _byob_accessory: '1', Accessory: a.title
        }});
      });
      const pendingItems = priced.items.concat(extras.map(a => ({id: a.id, quantity: a.quantity, selling_plan: ''})));
      // The teas (and optional accessories) go in on their own; the guarantee is sorted out afterwards.
      savePending({id, rbId, items: pendingItems, fingerprint});
      let addError = null;
      try {
        await post('cart/add.js', {items: lineItems.map(i => Object.assign({}, i, {id: Number(i.id)}))});
      } catch (error) { addError = error; }
      // Read back whatever happened: an add can fail after the lines landed, land partly, or succeed and not show.
      let cart = await request('cart.js');
      if (!linesPresent(cart, {id, rbId, items: pendingItems})) {
        cart = await removeLines(cart, id);
        savePending(null);
        throw new Error(addError ? 'The shop could not add your bundle. ' + addError.message :
          'Your bundle could not be verified. Please check your shop cart before trying again.');
      }
      savePending({id, rbId, items: pendingItems, fingerprint, verified: true});
      await applyPackingAttributes(cart);
      await settleProtection(cart, wantProtection, freshProtection);
      return {preview: false, items: lineItems, quote: priced, accessories: extras};
    } finally { busy = false; settleGuard(true); }
  }

  return Object.freeze({configure, quote, plansFor, oneTimeCents, checkout, goToCheckout, clearPending, isLive, protectionWanted, protectionPreview,
    protectionTierCents, mode: () => mode(), sizes: SIZES.slice(),
    _internal: {normaliseBundle, normaliseProtection, isProtectionLine, merchandiseCents, settleProtection, protectionUpdates, sharedPlanKeys, packingAttributes, builderGroups}});
});
